// Only UserMessageAttachments is referenced — backend has no upload
// endpoint, so the composer-side attachment slots are removed (see
// ComposerAction below). User-side attachment rendering is kept so
// historical messages that DO carry attachments still display.
import { UserMessageAttachments } from "@/components/assistant-ui/attachment";
import { MarkdownText } from "@/components/assistant-ui/markdown-text";
import { Sources } from "@/components/assistant-ui/sources";
import {
  Reasoning,
  ReasoningRoot,
  ReasoningTrigger,
  ReasoningContent,
  ReasoningText,
} from "@/components/assistant-ui/reasoning";
import {
  ToolGroupRoot,
  ToolGroupTrigger,
  ToolGroupContent,
} from "@/components/assistant-ui/tool-group";
import { DataPart } from "@/chat-v2/CustomParts";
import { ToolFallback } from "@/components/assistant-ui/tool-fallback";
import {
  SearchKnowledgeBaseInline,
  isSearchTool,
} from "@/chat-v2/SearchKnowledgeBaseInline";
import MessageRating from "@/components/MessageRating";
import { useState } from "react";
import { Box } from "@mui/material";
import type { ChatThreadMessage } from "@/chat-v2/messageMapper";
import {
  ChatComposerAbove,
  ChatComposerBelow,
} from "@/chat-v2/ChatComposerExtras";
import { ChatModeToggle } from "@/chat-v2/ChatModeToggle";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ActionBarMorePrimitive,
  ActionBarPrimitive,
  AuiIf,
  BranchPickerPrimitive,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  SuggestionPrimitive,
  ThreadPrimitive,
  getExternalStoreMessages,
  useAuiState,
} from "@assistant-ui/react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  DownloadIcon,
  MoreHorizontalIcon,
  PencilIcon,
  RefreshCwIcon,
  SquareIcon,
} from "lucide-react";
import type { FC } from "react";

export const Thread: FC = () => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root @container flex h-full flex-col bg-background"
      style={{
        // 120rem (~1920px) — user-driven widening 2026-05-18. Bumped
        // from 60rem so assistant answers (esp. Expert mode with long
        // markdown tables, code blocks, multi-section synthesis) get
        // the horizontal space they need. Bot bubble inherits the
        // 85% cap → ~102rem rendered. On viewports narrower than
        // 120rem the parent layout (mx-auto + px-4) clamps to the
        // visible column, so no overflow on mobile.
        ["--thread-max-width" as string]: "120rem",
        // 12px matches the legacy MUI composer (MuiPaper/MuiCard
        // rounding). 24px (assistant-ui default) read as "different
        // app", which broke the AGRANA visual language.
        ["--composer-radius" as string]: "12px",
        ["--composer-padding" as string]: "10px",
        ["--composer-max-width" as string]: "56rem",
      }}
    >
      <ThreadPrimitive.Viewport
        turnAnchor="top"
        data-slot="aui_thread-viewport"
        className="relative flex flex-1 flex-col overflow-x-auto overflow-y-scroll scroll-smooth"
      >
        <div className="mx-auto flex w-full max-w-(--thread-max-width) flex-1 flex-col px-4 pt-4">
          <AuiIf condition={(s) => s.thread.isEmpty}>
            <ThreadWelcome />
          </AuiIf>

          <div
            data-slot="aui_message-group"
            className="mb-10 flex flex-col gap-y-8 empty:hidden"
          >
            <ThreadPrimitive.Messages>
              {() => <ThreadMessage />}
            </ThreadPrimitive.Messages>
          </div>

          <ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mt-auto flex flex-col gap-4 overflow-visible rounded-t-(--composer-radius) bg-background pb-4 md:pb-6">
            <ThreadScrollToBottom />
            <ThreadPrimitive.If empty={false} running={false}>
              <FollowUpChips />
            </ThreadPrimitive.If>
            <ChatComposerAbove />
            <Composer />
            <ChatComposerBelow />
          </ThreadPrimitive.ViewportFooter>
        </div>
      </ThreadPrimitive.Viewport>
    </ThreadPrimitive.Root>
  );
};

const ThreadMessage: FC = () => {
  const role = useAuiState((s) => s.message.role);
  const isEditing = useAuiState((s) => s.message.composer.isEditing);

  if (isEditing) return <EditComposer />;
  if (role === "user") return <UserMessage />;
  return <AssistantMessage />;
};

const ThreadScrollToBottom: FC = () => {
  return (
    <ThreadPrimitive.ScrollToBottom asChild>
      <TooltipIconButton
        tooltip="Scroll to bottom"
        variant="outline"
        className="aui-thread-scroll-to-bottom absolute -top-12 z-10 self-center rounded-full p-4 disabled:invisible dark:border-border dark:bg-background dark:hover:bg-accent"
      >
        <ArrowDownIcon />
      </TooltipIconButton>
    </ThreadPrimitive.ScrollToBottom>
  );
};

const ThreadWelcome: FC = () => {
  // AGRANA-styled welcome: single centered block — title + suggestions
  // as one unit so they read as a unified empty state, not as two
  // floating sections separated by dead space.
  return (
    <div className="aui-thread-welcome-root my-auto flex w-full flex-col items-center justify-center gap-10 pb-12">
      <h1 className="fade-in slide-in-from-bottom-1 animate-in fill-mode-both text-center text-2xl font-semibold tracking-tight text-foreground duration-200">
        What can I help you with?
      </h1>
      <ThreadSuggestions />
    </div>
  );
};

const ThreadSuggestions: FC = () => {
  // Row of cards centered horizontally (legacy MUI flex pattern). On
  // narrow viewports they wrap to multiple rows.
  return (
    <div className="aui-thread-welcome-suggestions flex w-full flex-wrap justify-center gap-3 pb-4">
      <ThreadPrimitive.Suggestions>
        {() => <ThreadSuggestionItem />}
      </ThreadPrimitive.Suggestions>
    </div>
  );
};

const ThreadSuggestionItem: FC = () => {
  // Auto-height (no min-h) so cards size to content. Title:description
  // visual hierarchy via font-semibold/text-sm vs text-xs/muted.
  // Hover: subtle border tint, not full primary saturation.
  return (
    <div className="aui-thread-welcome-suggestion-display fade-in slide-in-from-bottom-2 w-full @md:w-[15rem] animate-in fill-mode-both duration-200">
      <SuggestionPrimitive.Trigger send asChild>
        <Button
          variant="ghost"
          className="aui-thread-welcome-suggestion h-auto w-full flex-col items-start justify-start gap-1.5 whitespace-normal rounded-lg border border-border bg-card px-4 py-3 text-start leading-snug shadow-xs transition-colors hover:border-primary/40 hover:bg-muted/60"
        >
          <SuggestionPrimitive.Title className="text-sm font-semibold text-foreground" />
          <SuggestionPrimitive.Description className="text-xs text-muted-foreground empty:hidden" />
        </Button>
      </SuggestionPrimitive.Trigger>
    </div>
  );
};

// Follow-up chips from the runtime's reactive `thread.suggestions`,
// rendered as singular (prompt-based) suggestions — not the static,
// index-based `ThreadPrimitive.Suggestions` scope.
const FollowUpChips: FC = () => {
  const suggestions = useAuiState((s) => s.thread.suggestions);
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div
      data-slot="aui-followup-chips"
      className="aui-followup-chips flex w-full flex-wrap items-start gap-2 px-1 pb-1 empty:hidden"
    >
      {suggestions.map((s, i) => (
        <ThreadPrimitive.Suggestion key={i} prompt={s.prompt} send asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "aui-followup-chip h-auto max-w-full whitespace-normal rounded-full border-border bg-card px-3 py-1.5 text-start text-xs font-normal leading-snug text-foreground/80 transition-colors",
              "hover:border-primary/40 hover:bg-muted/60 hover:text-foreground",
            )}
          >
            {s.prompt}
          </Button>
        </ThreadPrimitive.Suggestion>
      ))}
    </div>
  );
};

const Composer: FC = () => {
  return (
    <ComposerPrimitive.Root className="aui-composer-root relative mx-auto flex w-full max-w-(--composer-max-width) flex-col">
      {/* AttachmentDropzone + ComposerAttachments removed: the AI Hub
          backend has no file-upload endpoint, so a `+` button or a
          drop-zone highlight would be UI that promises something it
          can't deliver. If/when the backend gains uploads, wrap the
          shell in <ComposerPrimitive.AttachmentDropzone asChild> again
          and re-add <ComposerAttachments /> above the Input. */}
        <div
          data-slot="aui_composer-shell"
          className="flex w-full flex-col gap-2 rounded-(--composer-radius) border bg-background p-(--composer-padding) transition-shadow focus-within:border-ring/40 focus-within:ring-2 focus-within:ring-ring/15"
        >
          <ComposerPrimitive.Input
            placeholder="Ask AI Hub..."
            className="aui-composer-input max-h-32 min-h-10 w-full resize-none bg-transparent px-1.75 py-1 text-sm outline-none placeholder:text-muted-foreground/80"
            rows={1}
            autoFocus
            aria-label="Message input"
            // Matches backend's CHAT_MESSAGE_MAX_LEN. Upstream ANIS API
            // encodes the user message into the URL path; longer than
            // ~6KB and most edge servers return 414/404. Ported from
            // legacy Chatbot.tsx.
            maxLength={4000}
          />
          <ComposerAction />
        </div>
    </ComposerPrimitive.Root>
  );
};

const ComposerAction: FC = () => {
  // AGRANA-styled action row: Fast/Expert toggle on the right,
  // send/cancel after. Attachment button removed since backend has no
  // upload endpoint — see ComposerPrimitive.Root above.
  return (
    <div className="aui-composer-action-wrapper relative flex items-center justify-end gap-2">
      <ChatModeToggle />
      <AuiIf condition={(s) => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <TooltipIconButton
            tooltip="Send message"
            side="bottom"
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-send size-8 rounded-full"
            aria-label="Send message"
          >
            <ArrowUpIcon className="aui-composer-send-icon size-4" />
          </TooltipIconButton>
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={(s) => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="aui-composer-cancel size-8 rounded-full"
            aria-label="Stop generating"
          >
            <SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
          </Button>
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-destructive text-sm dark:bg-destructive/5 dark:text-red-200">
        <ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

// Rendered outside the parts pipeline — late-added parts (rating after
// stream complete) don't reach s.message.parts via external-store runtime.
// Reads original ChatMessage off ChatThreadMessage.__original — see messageMapper.
export const AssistantMessageRating: FC = () => {
  const status = useAuiState((s) => s.message.status);
  const externalMessages = useAuiState((s) =>
    getExternalStoreMessages<ChatThreadMessage>(s.message),
  );
  const [visible, setVisible] = useState(false);

  if (status?.type === "running") return null;
  const msg = externalMessages[externalMessages.length - 1];
  const original = msg?.__original;
  if (!original) return null;
  const content = (original.content ?? "").trim();
  if (!content) return null;
  if (content.toLowerCase().startsWith("error:") || content.startsWith("⚠️"))
    return null;
  // Prefer the BE-assigned id (post `complete` SSE) so the rating
  // POST hits the persisted row; fall back to the client UUID during
  // the narrow pre-complete window.
  const ratingId = original.serverMessageId ?? original.id;
  if (!ratingId) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <MessageRating
        messageId={ratingId}
        visible={visible}
        onToggleVisibility={() => setVisible((v) => !v)}
      />
    </Box>
  );
};

const AssistantMessage: FC = () => {
  // reserves space for action bar and compensates with `-mb` for consistent msg spacing
  // keeps hovered action bar from shifting layout (autohide doesn't support absolute positioning well)
  // for pt-[n] use -mb-[n + 6] & min-h-[n + 6] to preserve compensation
  const ACTION_BAR_PT = "pt-1.5";
  const ACTION_BAR_HEIGHT = `-mb-7.5 min-h-7.5 ${ACTION_BAR_PT}`;

  return (
    <MessagePrimitive.Root
      data-slot="aui_assistant-message-root"
      data-role="assistant"
      className="fade-in slide-in-from-bottom-1 relative animate-in duration-150 [contain-intrinsic-size:auto_300px] [content-visibility:auto]"
    >
      <div
        data-slot="aui_assistant-message-content"
        // Bot bubble — light muted background matching legacy MUI #f5f5f5
        // (shadcn `--muted` ≈ #f3f4f6). Max-width keeps long answers
        // narrow enough to read; user bubble already uses bg-muted.
        className="wrap-break-word max-w-[85%] rounded-2xl bg-muted/60 px-4 py-3 text-foreground leading-relaxed"
      >
        {/* @ts-expect-error React 18 FC return-type strictness vs library types written for React 19 */}
        <MessagePrimitive.GroupedParts
          groupBy={(part) => {
            if (part.type === "reasoning")
              return ["group-chainOfThought", "group-reasoning"];
            if (part.type === "tool-call")
              return ["group-chainOfThought", "group-tool"];
            return null;
          }}
        >
          {({ part, children }) => {
            switch (part.type) {
              case "group-chainOfThought":
                return <div data-slot="aui_chain-of-thought" className="my-2 flex flex-col gap-2">{children}</div>;
              case "group-reasoning": {
                const running = part.status.type === "running";
                return (
                  <ReasoningRoot defaultOpen={running} variant="muted">
                    <ReasoningTrigger active={running} />
                    <ReasoningContent aria-busy={running}>
                      <ReasoningText>{children}</ReasoningText>
                    </ReasoningContent>
                  </ReasoningRoot>
                );
              }
              case "group-tool":
                return (
                  <ToolGroupRoot variant="muted">
                    <ToolGroupTrigger
                      count={part.indices.length}
                      active={part.status.type === "running"}
                    />
                    <ToolGroupContent>{children}</ToolGroupContent>
                  </ToolGroupRoot>
                );
              case "text":
                return <MarkdownText />;
              case "reasoning":
                return <Reasoning {...part} />;
              case "tool-call":
                // RAG search tools get a compact one-line chip instead
                // of the generic accordion (much denser when there are
                // 3-5 search calls in a single chain-of-thought).
                if (isSearchTool(part.toolName)) {
                  return <SearchKnowledgeBaseInline {...part} />;
                }
                return part.toolUI ?? <ToolFallback {...part} />;
              case "source":
                return <Sources {...part} />;
              case "data":
                return <DataPart name={part.name} data={part.data} />;
              default:
                return null;
            }
          }}
        </MessagePrimitive.GroupedParts>
        <MessageError />
        <AssistantMessageRating />
      </div>

      <div
        data-slot="aui_assistant-message-footer"
        className={cn("ms-2 flex items-center", ACTION_BAR_HEIGHT)}
      >
        <BranchPicker />
        <AssistantActionBar />
      </div>
    </MessagePrimitive.Root>
  );
};

const AssistantActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-assistant-action-bar-root col-start-3 row-start-2 -ms-1 flex gap-1 text-muted-foreground"
    >
      <ActionBarPrimitive.Copy asChild>
        <TooltipIconButton tooltip="Copy">
          <AuiIf condition={(s) => s.message.isCopied}>
            <CheckIcon />
          </AuiIf>
          <AuiIf condition={(s) => !s.message.isCopied}>
            <CopyIcon />
          </AuiIf>
        </TooltipIconButton>
      </ActionBarPrimitive.Copy>
      <ActionBarPrimitive.Reload asChild>
        <TooltipIconButton tooltip="Refresh">
          <RefreshCwIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Reload>
      <ActionBarMorePrimitive.Root>
        <ActionBarMorePrimitive.Trigger asChild>
          <TooltipIconButton
            tooltip="More"
            className="data-[state=open]:bg-accent"
          >
            <MoreHorizontalIcon />
          </TooltipIconButton>
        </ActionBarMorePrimitive.Trigger>
        <ActionBarMorePrimitive.Content
          side="bottom"
          align="start"
          className="aui-action-bar-more-content z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <ActionBarPrimitive.ExportMarkdown asChild>
            <ActionBarMorePrimitive.Item className="aui-action-bar-more-item flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
              <DownloadIcon className="size-4" />
              Export as Markdown
            </ActionBarMorePrimitive.Item>
          </ActionBarPrimitive.ExportMarkdown>
        </ActionBarMorePrimitive.Content>
      </ActionBarMorePrimitive.Root>
    </ActionBarPrimitive.Root>
  );
};

const UserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      data-slot="aui_user-message-root"
      className="fade-in slide-in-from-bottom-1 grid animate-in auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 duration-150 [contain-intrinsic-size:auto_60px] [content-visibility:auto] [&:where(>*)]:col-start-2"
      data-role="user"
    >
      <UserMessageAttachments />

      <div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
        <div className="aui-user-message-content wrap-break-word peer rounded-2xl bg-muted px-4 py-2.5 text-foreground empty:hidden">
          <MessagePrimitive.Parts />
        </div>
        <div className="aui-user-action-bar-wrapper absolute start-0 top-1/2 -translate-x-full -translate-y-1/2 pe-2 peer-empty:hidden rtl:translate-x-full">
          <UserActionBar />
        </div>
      </div>

      <BranchPicker
        data-slot="aui_user-branch-picker"
        className="col-span-full col-start-1 row-start-3 -me-1 justify-end"
      />
    </MessagePrimitive.Root>
  );
};

const UserActionBar: FC = () => {
  return (
    <ActionBarPrimitive.Root
      hideWhenRunning
      autohide="not-last"
      className="aui-user-action-bar-root flex flex-col items-end"
    >
      <ActionBarPrimitive.Edit asChild>
        <TooltipIconButton tooltip="Edit" className="aui-user-action-edit p-4">
          <PencilIcon />
        </TooltipIconButton>
      </ActionBarPrimitive.Edit>
    </ActionBarPrimitive.Root>
  );
};

const EditComposer: FC = () => {
  return (
    <MessagePrimitive.Root
      data-slot="aui_edit-composer-wrapper"
      className="flex flex-col px-2"
    >
      <ComposerPrimitive.Root className="aui-edit-composer-root ms-auto flex w-full max-w-[85%] flex-col rounded-2xl bg-muted">
        <ComposerPrimitive.Input
          className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent p-4 text-foreground text-sm outline-none"
          autoFocus
        />
        <div className="aui-edit-composer-footer mx-3 mb-3 flex items-center gap-2 self-end">
          <ComposerPrimitive.Cancel asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </ComposerPrimitive.Cancel>
          <ComposerPrimitive.Send asChild>
            <Button size="sm">Update</Button>
          </ComposerPrimitive.Send>
        </div>
      </ComposerPrimitive.Root>
    </MessagePrimitive.Root>
  );
};

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
  className,
  ...rest
}) => {
  return (
    <BranchPickerPrimitive.Root
      hideWhenSingleBranch
      className={cn(
        "aui-branch-picker-root -ms-2 me-2 inline-flex items-center text-muted-foreground text-xs",
        className,
      )}
      {...rest}
    >
      <BranchPickerPrimitive.Previous asChild>
        <TooltipIconButton tooltip="Previous">
          <ChevronLeftIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Previous>
      <span className="aui-branch-picker-state font-medium">
        <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
      </span>
      <BranchPickerPrimitive.Next asChild>
        <TooltipIconButton tooltip="Next">
          <ChevronRightIcon />
        </TooltipIconButton>
      </BranchPickerPrimitive.Next>
    </BranchPickerPrimitive.Root>
  );
};
